import logosIcons from "@iconify-json/logos/icons.json";
import mdiIcons from "@iconify-json/mdi/icons.json";
import { getIconData, iconToHTML, iconToSVG } from "@iconify/utils";

type IconifyJSON = Parameters<typeof getIconData>[0];

type IconSource =
	| {
			set: "logos";
			name: string;
			/** Force monochrome marks onto a readable color for dark UI. */
			paint?: string;
			recolorFrom?: string | string[];
			recolorTo?: string;
	  }
	| {
			set: "mdi";
			name: string;
	  }
	| {
			/** Inline path data for marks not in Iconify (e.g. Resend). */
			set: "custom";
			viewBox: string;
			path: string;
	  };

export type PlatformMark = {
	id: string;
	label: string;
	color: string;
	svg: string;
};

const logos = logosIcons as IconifyJSON;
const mdi = mdiIcons as IconifyJSON;

const catalog: Array<{
	id: string;
	label: string;
	color: string;
	source: IconSource;
	/** Omit from the homepage platform marquee. */
	hideFromRail?: boolean;
}> = [
	{
		id: "instagram",
		label: "Instagram",
		color: "#FF0069",
		source: {
			set: "logos",
			name: "instagram-icon",
			recolorFrom: "#0a0a08",
			recolorTo: "#FF0069",
		},
	},
	{
		id: "facebook",
		label: "Facebook",
		color: "#1877F2",
		source: { set: "logos", name: "facebook" },
	},
	{
		id: "tiktok",
		label: "TikTok",
		color: "#FE2C55",
		source: { set: "logos", name: "tiktok-icon" },
	},
	{
		id: "x",
		label: "X",
		color: "#E7E9EA",
		source: { set: "logos", name: "x", paint: "#E7E9EA" },
	},
	{
		id: "youtube",
		label: "YouTube",
		color: "#FF0000",
		source: { set: "logos", name: "youtube-icon" },
	},
	{
		id: "linkedin",
		label: "LinkedIn",
		color: "#0A66C2",
		source: { set: "logos", name: "linkedin-icon" },
	},
	{
		id: "threads",
		label: "Threads",
		color: "#FFFFFF",
		source: { set: "logos", name: "threads-icon", paint: "#FFFFFF" },
	},
	{
		id: "pinterest",
		label: "Pinterest",
		color: "#E60023",
		source: { set: "logos", name: "pinterest" },
	},
	{
		id: "reddit",
		label: "Reddit",
		color: "#FF4500",
		source: { set: "logos", name: "reddit-icon" },
	},
	{
		id: "gbp",
		label: "Google Business",
		color: "#4285F4",
		source: { set: "mdi", name: "google-my-business" },
	},
	{
		id: "ads",
		label: "Google Ads",
		color: "#FF8A3D",
		source: { set: "logos", name: "google-ads" },
	},
	{
		id: "analytics",
		label: "Google Analytics",
		color: "#F9AB00",
		source: { set: "logos", name: "google-analytics" },
	},
	{
		id: "searchconsole",
		label: "Search Console",
		color: "#458CF5",
		source: { set: "logos", name: "google-search-console" },
	},
	{
		id: "tagmanager",
		label: "Tag Manager",
		color: "#246FDB",
		source: { set: "logos", name: "google-tag-manager" },
	},
	{
		id: "meta",
		label: "Meta",
		color: "#0668E1",
		source: { set: "logos", name: "meta-icon" },
	},
	{
		id: "slack",
		label: "Slack",
		color: "#E01E5A",
		source: { set: "logos", name: "slack-icon" },
	},
	{
		id: "whatsapp",
		label: "WhatsApp",
		color: "#25D366",
		source: { set: "logos", name: "whatsapp-icon" },
	},
	{
		id: "teams",
		label: "Microsoft Teams",
		color: "#6264A7",
		source: { set: "logos", name: "microsoft-teams" },
	},
	{
		id: "gmail",
		label: "Gmail",
		color: "#EA4335",
		source: { set: "logos", name: "google-gmail" },
	},
	{
		id: "meet",
		label: "Google Meet",
		color: "#00897B",
		source: { set: "logos", name: "google-meet" },
	},
	{
		id: "shopify",
		label: "Shopify",
		color: "#96BF48",
		source: { set: "logos", name: "shopify" },
	},
	{
		id: "wordpress",
		label: "WordPress",
		color: "#4A9FD8",
		source: {
			set: "logos",
			name: "wordpress-icon",
			recolorFrom: ["#464342", "#000", "#000000"],
			recolorTo: "#4A9FD8",
		},
	},
	{
		id: "webflow",
		label: "Webflow",
		color: "#146EF5",
		source: {
			set: "custom",
			viewBox: "0 0 24 24",
			// Square Webflow mark (Simple Icons) — logos:webflow is a wide wordmark
			path: "m24 4.515-7.658 14.97H9.149l3.205-6.204h-.144C9.566 16.713 5.621 18.973 0 19.485v-6.118s3.596-.213 5.71-2.435H0V4.515h6.417v5.278l.144-.001 2.622-5.277h4.854v5.244h.144l2.72-5.244H24Z",
		},
	},
	{
		id: "wix",
		label: "Wix",
		color: "#0C6EFC",
		source: { set: "logos", name: "wix", paint: "#0C6EFC" },
	},
	{
		id: "squarespace",
		label: "Squarespace",
		color: "#FFFFFF",
		source: {
			set: "logos",
			name: "squarespace",
			recolorFrom: ["#1a1918", "#000", "#000000"],
			recolorTo: "#FFFFFF",
		},
	},
	{
		id: "github",
		label: "GitHub",
		color: "#FFFFFF",
		source: { set: "logos", name: "github-icon", paint: "#FFFFFF" },
	},
	{
		id: "vercel",
		label: "Vercel",
		color: "#FFFFFF",
		source: {
			set: "custom",
			viewBox: "0 0 24 24",
			// Simple Icons Vercel mark
			path: "M24 22.525H0l12-21.05 12 21.05z",
		},
	},
	{
		id: "twilio",
		label: "Twilio",
		color: "#F22F46",
		source: { set: "logos", name: "twilio-icon" },
	},
	{
		id: "telegram",
		label: "Telegram",
		color: "#26A5E4",
		source: { set: "logos", name: "telegram" },
	},
	{
		id: "clickup",
		label: "ClickUp",
		color: "#7B68EE",
		source: {
			set: "custom",
			viewBox: "0 0 24 24",
			// Simple Icons ClickUp mark
			path: "M2 18.439l3.69-2.213 15.92 9.036v-3.565L5.69 12.66 2 14.876v3.564zm0-7.328l3.69 2.213 15.92-9.038V.72L5.69 6.5 2 4.286v6.825z",
		},
	},
	{
		id: "linear",
		label: "Linear",
		color: "#5E6AD2",
		source: { set: "logos", name: "linear-icon" },
	},
	{
		id: "godaddy",
		label: "GoDaddy",
		color: "#1BDBDB",
		source: {
			set: "custom",
			viewBox: "0 0 24 24",
			// Simple Icons GoDaddy mark
			path: "M20.702 2.29c-2.494-1.554-5.778-1.187-8.706.654C9.076 1.104 5.79.736 3.3 2.29c-3.941 2.463-4.42 8.806-1.07 14.167 2.47 3.954 6.333 6.269 9.77 6.226 3.439.043 7.301-2.273 9.771-6.226 3.347-5.361 2.872-11.704-1.069-14.167zM4.042 15.328a12.838 12.838 0 01-1.546-3.541 10.12 10.12 0 01-.336-3.338c.15-1.98.956-3.524 2.27-4.345 1.315-.822 3.052-.87 4.903-.137.281.113.556.24.825.382A15.11 15.11 0 007.5 7.54c-2.035 3.255-2.655 6.878-1.945 9.765a13.247 13.247 0 01-1.514-1.98zm17.465-3.541a12.866 12.866 0 01-1.547 3.54 13.25 13.25 0 01-1.513 1.984c.635-2.589.203-5.76-1.353-8.734a.39.39 0 00-.563-.153l-4.852 3.032a.397.397 0 00-.126.546l.712 1.139a.395.395 0 00.547.126l3.145-1.965c.101.306.203.606.28.916.296 1.086.41 2.214.335 3.337-.15 1.982-.956 3.525-2.27 4.347a4.437 4.437 0 01-2.25.65h-.101a4.432 4.432 0 01-2.25-.65c-1.314-.822-2.121-2.365-2.27-4.347-.074-1.123.039-2.251.335-3.337a13.212 13.212 0 014.05-6.482 10.148 10.148 0 012.849-1.765c1.845-.733 3.586-.685 4.9.137 1.316.822 2.122 2.365 2.271 4.345a10.146 10.146 0 01-.33 3.334z",
		},
		hideFromRail: true,
	},
	{
		id: "namecheap",
		label: "Namecheap",
		color: "#DE3723",
		source: { set: "logos", name: "namecheap" },
		hideFromRail: true,
	},
	{
		id: "cloudflare",
		label: "Cloudflare",
		color: "#F6821F",
		source: { set: "logos", name: "cloudflare-icon" },
		hideFromRail: true,
	},
	{
		id: "googledomains",
		label: "Google Domains",
		color: "#4285F4",
		source: { set: "logos", name: "google-domains-icon" },
		hideFromRail: true,
	},
	{
		id: "route53",
		label: "Route 53",
		color: "#8C4FFF",
		source: { set: "logos", name: "aws-route53" },
		hideFromRail: true,
	},
	{
		id: "letsencrypt",
		label: "Let's Encrypt",
		color: "#003A70",
		source: { set: "logos", name: "letsencrypt" },
		hideFromRail: true,
	},
	{
		id: "domain",
		label: "Domain",
		color: "#60CFFB",
		source: { set: "mdi", name: "web" },
		hideFromRail: true,
	},
	{
		id: "hubspot",
		label: "HubSpot",
		color: "#FF7A59",
		source: {
			set: "logos",
			name: "hubspot",
			recolorFrom: ["#45535e", "#ff7a59", "#f8761f"],
			recolorTo: "#FF7A59",
		},
	},
	{
		id: "mailchimp",
		label: "Mailchimp",
		color: "#FFE01B",
		source: {
			set: "logos",
			name: "mailchimp",
			recolorFrom: ["#0b0b0a", "#0b0c0a", "#000", "#000000"],
			recolorTo: "#FFE01B",
		},
	},
	{
		id: "klaviyo",
		label: "Klaviyo",
		color: "#FFFFFF",
		source: {
			set: "custom",
			viewBox: "0 0 24 24",
			// Classic Klaviyo lettermark (K) — white for dark UI
			path: "M7.2 4.8h5.747c2.34 0 3.895 1.406 3.895 3.516c0 1.022-.348 1.862-1.09 2.588C17.189 11.812 18 13.22 18 14.785c0 2.86-2.64 5.016-6.164 5.016H7.199v-15zm2.085 1.952v5.537h.07c.233-.432.858-.796 2.249-1.226c2.039-.659 3.037-1.52 3.037-2.655c0-.998-.766-1.656-1.924-1.656zm4.87 5.266c-.766.385-1.67.748-2.76 1.11c-1.229.387-2.11 1.386-2.11 2.407v2.315h2.365c2.387 0 4.149-1.34 4.149-3.155c0-1.067-.625-2.087-1.645-2.677z",
		},
	},
	{
		id: "resend",
		label: "Resend",
		color: "#FFFFFF",
		source: {
			set: "custom",
			viewBox: "0 0 1800 1800",
			// Official lettermark path from cdn.resend.com/brand/resend-icon-white.svg
			path: "M1000.46 450C1174.77 450 1278.43 553.669 1278.43 691.282C1278.43 828.896 1174.77 932.563 1000.46 932.563H912.382L1350 1350H1040.82L707.794 1033.48C683.944 1011.47 672.936 985.781 672.935 963.765C672.935 932.572 694.959 905.049 737.161 893.122L908.712 847.244C973.85 829.812 1018.81 779.353 1018.81 713.298C1018.8 632.567 952.745 585.78 871.095 585.78H450V450H1000.46Z",
		},
	},
	{
		id: "sendgrid",
		label: "SendGrid",
		color: "#1A82E2",
		source: { set: "logos", name: "sendgrid-icon" },
	},
	{
		id: "braze",
		label: "Braze",
		color: "#FFFFFF",
		source: { set: "logos", name: "braze-icon", paint: "#FFFFFF" },
	},
	{
		id: "customerio",
		label: "Customer.io",
		color: "#7131FF",
		source: { set: "logos", name: "customerio-icon" },
	},
	{
		id: "activecampaign",
		label: "ActiveCampaign",
		color: "#004CFF",
		source: { set: "logos", name: "active-campaign-icon" },
	},
	{
		id: "mailgun",
		label: "Mailgun",
		color: "#F06B66",
		source: { set: "logos", name: "mailgun-icon" },
	},
	{
		id: "campaignmonitor",
		label: "Campaign Monitor",
		color: "#7856FF",
		source: { set: "logos", name: "campaignmonitor-icon" },
	},
	{
		id: "mailjet",
		label: "Mailjet",
		color: "#9585F4",
		source: { set: "logos", name: "mailjet-icon" },
	},
	{
		id: "sparkpost",
		label: "SparkPost",
		color: "#FA6423",
		source: { set: "logos", name: "sparkpost" },
	},
	{
		id: "figma",
		label: "Figma",
		color: "#F24E1E",
		source: { set: "logos", name: "figma" },
	},
	{
		id: "googledrive",
		label: "Google Drive",
		color: "#4285F4",
		source: { set: "logos", name: "google-drive" },
	},
	{
		id: "googlephotos",
		label: "Google Photos",
		color: "#FABC05",
		source: { set: "logos", name: "google-photos" },
	},
	{
		id: "canva",
		label: "Canva",
		color: "#00C4CC",
		source: { set: "logos", name: "canva-icon" },
	},
	{
		id: "dropbox",
		label: "Dropbox",
		color: "#0061FF",
		source: { set: "logos", name: "dropbox" },
	},
	{
		id: "framer",
		label: "Framer",
		color: "#FFFFFF",
		source: { set: "logos", name: "framer", paint: "#FFFFFF" },
	},
	{
		id: "notion",
		label: "Notion",
		color: "#FFFFFF",
		source: { set: "logos", name: "notion-icon", paint: "#FFFFFF" },
	},
	{
		id: "asana",
		label: "Asana",
		color: "#F06A6A",
		source: { set: "logos", name: "asana-icon" },
	},
	{
		id: "trello",
		label: "Trello",
		color: "#0052CC",
		source: { set: "logos", name: "trello" },
	},
	{
		id: "zapier",
		label: "Zapier",
		color: "#FF4A00",
		source: { set: "logos", name: "zapier-icon" },
	},
	{
		id: "intercom",
		label: "Intercom",
		color: "#6AFDEF",
		source: { set: "logos", name: "intercom-icon", paint: "#6AFDEF" },
	},
	{
		id: "stripe",
		label: "Stripe",
		color: "#635BFF",
		source: { set: "logos", name: "stripe" },
	},
	{
		id: "discord",
		label: "Discord",
		color: "#5865F2",
		source: { set: "logos", name: "discord-icon" },
	},
	{
		id: "buffer",
		label: "Buffer",
		color: "#168EEA",
		source: { set: "logos", name: "buffer", paint: "#168EEA" },
	},
	{
		id: "hootsuite",
		label: "Hootsuite",
		color: "#FF4C46",
		source: { set: "logos", name: "hootsuite-icon" },
	},
	{
		id: "apple",
		label: "App Store",
		color: "#F5F5F7",
		source: { set: "logos", name: "apple", paint: "#F5F5F7" },
	},
	{
		id: "android",
		label: "Google Play",
		color: "#3DDC84",
		source: { set: "logos", name: "android-icon" },
	},
	{
		id: "openai",
		label: "OpenAI",
		color: "#FFFFFF",
		source: { set: "logos", name: "openai-icon", paint: "#FFFFFF" },
	},
	{
		id: "anthropic",
		label: "Anthropic",
		color: "#D4A27F",
		source: {
			set: "logos",
			name: "anthropic-icon",
			recolorFrom: ["#181818", "#000", "#000000"],
			recolorTo: "#D4A27F",
		},
	},
];

function normalizeHex(value: string): string {
	return value.toLowerCase().replace(/\s/g, "");
}

function isNearBlack(value: string): boolean {
	const v = normalizeHex(value);
	if (v === "black" || v === "#000" || v === "#000000") return true;
	const match = /^#([0-9a-f]{6})$/i.exec(v);
	const hex = match?.[1];
	if (!hex) return false;
	const r = Number.parseInt(hex.slice(0, 2), 16);
	const g = Number.parseInt(hex.slice(2, 4), 16);
	const b = Number.parseInt(hex.slice(4, 6), 16);
	return r < 40 && g < 40 && b < 40;
}

function paintBody(body: string, color: string): string {
	let next = body.replace(/fill="([^"]+)"/gi, (match, value: string) => {
		const normalized = normalizeHex(value);
		if (normalized.startsWith("url(")) return match;
		return `fill="${color}"`;
	});
	next = next.replace(/<path\b(?![^>]*\bfill=)/gi, `<path fill="${color}"`);
	return next;
}

function recolorBody(body: string, from: string | string[], to: string): string {
	const targets = new Set(
		(Array.isArray(from) ? from : [from]).map((value) => normalizeHex(value)),
	);

	let next = body.replace(/fill="([^"]+)"/gi, (match, value: string) => {
		const normalized = normalizeHex(value);
		if (targets.has(normalized) || (targets.has("#000") && isNearBlack(normalized))) {
			return `fill="${to}"`;
		}
		return match;
	});

	if (!/fill=/.test(next)) {
		next = next.replace(/<path\b(?![^>]*\bfill=)/gi, `<path fill="${to}"`);
	}

	return next;
}

function renderSource(source: IconSource, size: number, color: string): string | null {
	switch (source.set) {
		case "custom": {
			const body = `<path fill="${color}" d="${source.path}"/>`;
			return iconToHTML(body, {
				xmlns: "http://www.w3.org/2000/svg",
				viewBox: source.viewBox,
				width: String(size),
				height: String(size),
				"aria-hidden": "true",
			});
		}
		case "logos":
		case "mdi": {
			const collection = source.set === "logos" ? logos : mdi;
			const data = getIconData(collection, source.name);
			if (!data) return null;

			const svg = iconToSVG(data, { height: `${size}px` });
			let body = svg.body;

			if (source.set === "mdi") {
				body = body.replaceAll("currentColor", color);
			} else if (source.paint) {
				body = paintBody(body, source.paint);
			} else if (source.recolorFrom && source.recolorTo) {
				body = recolorBody(body, source.recolorFrom, source.recolorTo);
			}

			const attrs: Record<string, string> = {
				...svg.attributes,
				xmlns: "http://www.w3.org/2000/svg",
				width: String(size),
				height: String(size),
				"aria-hidden": "true",
			};
			if (source.set === "mdi") {
				attrs.fill = color;
			}

			return iconToHTML(body, attrs);
		}
		default: {
			const _exhaustive: never = source;
			void _exhaustive;
			return null;
		}
	}
}

export function getPlatformMark(id: string, size = 18): PlatformMark | null {
	const entry = catalog.find((candidate) => candidate.id === id);
	if (!entry) return null;
	const svg = renderSource(entry.source, size, entry.color);
	if (!svg) return null;
	return { id: entry.id, label: entry.label, color: entry.color, svg };
}

/** Inline MDI glyph for UI chrome (hearts, votes, etc.). */
export function getUiIcon(name: string, size = 18, color = "#262626"): string | null {
	return renderSource({ set: "mdi", name }, size, color);
}

export function getPlatformMarks(size = 22): PlatformMark[] {
	const marks: PlatformMark[] = [];
	for (const entry of catalog) {
		if (entry.hideFromRail) continue;
		const svg = renderSource(entry.source, size, entry.color);
		if (!svg) continue;
		marks.push({
			id: entry.id,
			label: entry.label,
			color: entry.color,
			svg,
		});
	}
	return marks;
}
